import { useSelector } from 'react-redux';
import { useDocumentation } from '../../useDocumentation';
import { selectAccountUsers } from '../../../../reducers/accountUsersReducer';
import { selectEnvironments, selectParsedFields } from '../../../../reducers/global/globalReducer';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import { ruleByObjectName } from '../../../parser/parserUtils';
import {
  checkIfActive,
  createConfigurationItemFromAutomation,
  createConfigurationItemFromRecordType,
} from '../../helpers';
import { ConfigurationItem } from '../DependenciesTypes';
import { GlobalData } from '../../../../reducers/global/globalReducerTypes';
import pick from 'lodash/pick';
import { DeployStatusForTable } from '../../../../types/enums/DeployStatusForTable';
import { ConfigurationType } from '../types';
import { useGetRelatedObjects } from '../../selected-object/useGetRelatedObjects';
import { useMemo } from 'react';
import { MASTER_KEY } from '../../../pages/funnel-map-flow/dialogs/import-funnel/utils';

interface UseGetObjectDependenciesProps {
  crmOrgId: string;
  objectName: string;
  isSupportedType: boolean;
  onlyActive: boolean;
}

export const useGetObjectDependencies = ({
  crmOrgId,
  objectName,
  isSupportedType,
  onlyActive,
}: UseGetObjectDependenciesProps) => {
  const { parsedRules, parsedRecordTypes } = useDocumentation();
  const users = useSelector(selectAccountUsers);
  const globalEnvs = useSelector(selectEnvironments);
  const fields = useSelector(selectParsedFields(crmOrgId));
  const relatedObjects = useGetRelatedObjects(crmOrgId, objectName);
  const relatedObjectsWithMappedObjects = useMemo(() => {
    return relatedObjects.map((relatedObject) => ({
      ...relatedObject,
      objectName: relatedObject.objectName ?? relatedObject.referencedObjects?.[0] ?? '',
    }));
  }, [relatedObjects]);

  if (!isSupportedType) {
    return {};
  }

  const attributions = {
    [ConfigurationType.fields]: fields?.[objectName] ?? [],
    [ConfigurationType.relatedObjects]: relatedObjectsWithMappedObjects ?? [],
  } as ConfigurationItemMap;

  parsedRules?.forEach((rule) => {
    if (ruleByObjectName(objectName)(rule)) {
      const type = rule.type;

      if (!attributions[type]) {
        attributions[type] = [];
      }

      if (checkIfActive(onlyActive, rule)) {
        attributions[type].push(rule as ConfigurationItem);
      }
    }
  });

  parsedRecordTypes?.forEach((recordType) => {
    if (recordType.objectApiName === objectName && recordType.name !== MASTER_KEY) {
      const type = ConfigurationType.recordTypes;

      if (!attributions[type]) {
        attributions[type] = [];
      }

      if (checkIfActive(onlyActive, recordType)) {
        attributions[type].push(
          createConfigurationItemFromRecordType(recordType) as ConfigurationItem,
        );
      }
    }
  });

  //adding sweep elements
  const sweepElementsAttributions = {} as ConfigurationItemMap;
  const elements = globalEnvs?.[crmOrgId]?.data;
  const automationElements: Partial<GlobalData> = pick(
    elements ?? {},
    'automations',
    'alerts',
    'scheduledAssignments',
    'assignments',
    'dedup',
    'matching',
  );

  Object.entries(automationElements ?? {})?.forEach(([key, values]) => {
    sweepElementsAttributions[key as keyof ConfigurationItemMap] = [];
    (values as AutomationStructureNew[]).forEach((automation) => {
      if (
        ![DeployStatusForTable.Pending, DeployStatusForTable.Deployed].includes(
          automation.status,
        ) ||
        automation.objectName !== objectName
      ) {
        return;
      }

      if (automation.objectName === objectName) {
        const configurationAutomation = createConfigurationItemFromAutomation(automation, users);

        if (configurationAutomation) {
          sweepElementsAttributions[key as keyof ConfigurationItemMap].push(
            configurationAutomation,
          );
        }
      }
    });
  });

  return { attributions, sweepElementsAttributions };
};
